import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { uniqueArray } from 'src/helpers/dataHelpers';
import { DICTItem } from 'src/pages/app/_BLL/metaDictionaries/types';
import { GeneratedField } from 'src/pages/CompanyPage/_BLL/types';
import { getFieldNameCompany } from 'src/pages/CompanyPage/lib/getFieldNameCompany';
import { Select } from 'src/shared/ui/_inputs/Select';
import { InfoTooltip } from 'src/shared/ui/_tooltips/InfoTooltip';
import s from './DropDownManyField.module.scss';

interface Props extends GeneratedField {}

export const DropDownManyField: React.FC<Props> = (props) => {
	const {
		namePrefix, //
		propertyName,
		internalName,
		validate,
		fieldDictionary,
	} = props;

	// * Form
	const COMMAND__SELECT_All = 'COMMAND__SELECT_All';
	const COMMAND__DESELECT = 'COMMAND__DESELECT';

	const { setValue, control } = useFormContext();

	const NAME = getFieldNameCompany({ namePrefix, propertyName, internalName });

	// ! Industry and sector ONLY...
	// - Primary
	const sectorName = 'properties.industryAndSector.sector';
	const industryName = 'properties.industryAndSector.industry';

	const sectorValues: string[] | null = useWatch({
		name: `${sectorName}.values`,
	});

	const industryValues: string[] | null = useWatch({
		name: `${industryName}.values`,
	});
	// ! ...Industry and sector ONLY

	const handleChange = (fieldName: string, values: Array<string | string[]>) => {
		const toUniqueFlatArray = (values: Array<string | string[]>) => {
			return uniqueArray(values.flat());
		};

		if (values.length === 0 || values.find((value) => value === COMMAND__DESELECT)) {
			setValue(fieldName, null);

			// ! Industry and sectors ONLY...
			// When primary sector value changes nullify primary industry.
			if (NAME === sectorName) {
				setValue(`${industryName}.values`, null);
			}
			// ! ...Industry and sectors ONLY.
		} else if (values.find((value) => value === COMMAND__SELECT_All)) {
			const allValues = fieldDictionary ? dictionaryFilter(fieldDictionary).map((dictionary) => String(dictionary.id)) : [];
			setValue(fieldName, allValues);
		} else {
			setValue(fieldName, toUniqueFlatArray(values));

			// ! Industry and sectors ONLY...
			filterIndustries(values.flat());
			// ! ...Industry and sectors ONLY
		}
	};

	// ! Sector only ...
	// The function removes existing sectors when the industry is removed from selected.
	// Example: If sector Agriculture is removed the industries of the agriculture also removed.
	// TODO: Remove selected in primary sector.
	const filterIndustries = (sectorValues: string[]) => {
		if (sectorValues && industryValues && NAME === sectorName) {
			let filteredIndustryValues: string[] = [];

			for (const value of sectorValues) {
				filteredIndustryValues = [
					...filteredIndustryValues,
					...industryValues.filter((industryValue) => industryValue.substring(0, 2) === value.substring(0, 2) && industryValue.length === value.length),
				];
			}

			if (industryValues.length === 0) {
				setValue(`${industryName}.values`, null);
			} else {
				setValue(`${industryName}.values`, uniqueArray(filteredIndustryValues));
			}
		}
	};
	// ! ...

	const dictionaryFilter = (dictionary: DICTItem[]): DICTItem[] => {
		if (NAME === industryName) {
			if (sectorValues) {
				let filteredDictionary: DICTItem[] = [];

				// return only the industries that related to selected sectors.
				for (const value of sectorValues) {
					filteredDictionary = [...filteredDictionary, ...dictionary.filter((item) => String(item.id).slice(0, -3) + '000' === value)];
				}

				return filteredDictionary;
			} else {
				return [];
			}
		} else {
			return dictionary;
		}
	};

	// * Conditions
	// ! Industries and sectors ONLY ...
	const disableIndustry = NAME === industryName && sectorValues === null;
	// ! ...
	const fieldDisabled = disableIndustry;

	// * Render
	return (
		<Controller
			name={`${NAME}.values`}
			control={control}
			rules={{ validate: validate }}
			render={({ field, fieldState }) => (
				<div className={s.fieldWrapper}>
					<Select
						{...field}
						className={s.field}
						mode="multiple"
						maxTagCount="responsive"
						placement="bottomLeft"
						filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
						options={
							fieldDictionary && [
								{ value: COMMAND__SELECT_All, label: 'Select All' },
								{ value: COMMAND__DESELECT, label: 'Deselect All' },
								...dictionaryFilter(fieldDictionary).map((dictionary) => ({ value: String(dictionary.id), label: dictionary.name })),
							]
						}
						onChange={(value) => {
							handleChange(field.name, value);
						}}
						disabled={fieldDisabled}
						error={fieldState.error?.message}
						size="middle"
					/>

					<InfoTooltip
						info={(field.value ?? []).map((value: string | number) =>
							fieldDictionary
								? fieldDictionary?.find((dictItem) => String(dictItem.id) === String(value))?.name //
								: value,
						)}
					/>
				</div>
			)}
		/>
	);
};
